<template>
	<div class="scene flex flex-col items-center justify-center s-fill">
		<!-- Scene background Video/SVG -->
		<div class="z-0 flex flex-col items-center justify-center s-fill" v-if="scene && assets && assets[scene.node_content]">
			<div
			class="absolute flex flex-col items-center justify-center s-fill faa-fade"
			:class="{ 'faa-fade-in visible': (currentSceneIndex == 0), 'invisible': currentSceneIndex != 0 }">
				<video
				autoplay playsinline disablePictureInPicture
				v-if="getType(scenes[0]) == 'video'"
				ref="video0"
				:controls="!hasBeenPlayed"
				controlslist="nodownload nofullscreen"
				@durationchange="checkPlayedState"
				@loadeddata="loadedData(0)"
				@play="updatePlayState(0)"
				@playing="updatePlayState(0)"
				@pause="updatePlayState(0)"
				@ended="sceneEnded">
					<source :src="assets[scenes[0].node_content].url" type="video/mp4">
				</video>

				<img class="s-fit" v-else-if="getType(scenes[0]) == 'static'" :src="assets[scenes[0].node_content].url" @load="loadedData(0)">
			</div>

			<div
			class="absolute flex flex-col items-center justify-center s-fill faa-fade"
			:class="{ 'faa-fade-in visible': (currentSceneIndex == 1), 'invisible': currentSceneIndex != 1 }">
				<video
				autoplay playsinline disablePictureInPicture
				v-if="getType(scenes[1]) == 'video'"
				ref="video1"
				:controls="!hasBeenPlayed"
				controlslist="nodownload nofullscreen"
				@durationchange="checkPlayedState"
				@loadeddata="loadedData(1)"
				@play="updatePlayState(1)"
				@playing="updatePlayState(1)"
				@pause="updatePlayState(1)"
				@ended="sceneEnded">
					<source :src="assets[scenes[1].node_content].url" type="video/mp4">
				</video>

				<img class="s-fit" v-else-if="getType(scenes[1]) == 'static'" :src="assets[scenes[1].node_content].url" @load="loadedData(1)">
			</div>

			<!-- Scene title -->
			<p
			v-if="getType(scene) == 'video' && showTitle && scene.title"
			class="absolute m-auto rounded bg-white-o7 border 
			border-blue-dark font-main-medium text-2xl text-blue-dark 
			text-center px-10 py-2 mt-4 ml-4 faa-fade-inout"
			style="top: 45px"
			>
				{{scene.title}}
			</p>
		</div>
	</div>
</template>

<script>
	import { mapState } from 'vuex'

	export default {
		name: 'Scene',
		props: {
			scene: {
				type: Object,
				required: false,
				default: null
			},
			hasBeenPlayed: {
				type: Boolean,
				required: false,
				default: false
			},
		},
		data() {
			return {
				showTitle: false,
				isPlaying: false,
				currentSceneIndex: -1,
				nextSceneIndex: -1,
				scenes: {
					0: {},
					1: {},
				}
			}
		},
		computed: {
			...mapState('Assets', {
				assets(state) {
					if (!state.list || state.list.length <= 0)
						return {}

					return state.list.reduce((dict, asset) => {
						dict[asset.id] = asset

						return dict
					}, {})
				},
			}),
		},
		watch: {
			scene: {
				handler(scene) {
					// Reset states
					this.showTitle = false
					this.isPlaying = false

					if (!scene)
						return

					// Set scenes
					if (this.currentSceneIndex == 0) {
						this.$emit('loading')

						this.$set(this.scenes, 1, scene)
						this.nextSceneIndex = 1

						// Force sources update/reload
						if (this.$refs.video1)
							this.$refs.video1.load()
					} else {
						this.$emit('loading')

						this.$set(this.scenes, 0, scene)
						this.nextSceneIndex = 0

						// Force sources update/reload
						if (this.$refs.video0)
							this.$refs.video0.load()
					}

					// Title animation
					this.$nextTick(() => {
						this.showTitle = true
					})
				},
				immediate: true,
			},
			isPlaying: {
				handler(isPlaying) {
					this.$emit('playing', isPlaying)
				},
				immediate: true,
			},
			hasBeenPlayed: {
				handler() {
					this.checkPlayedState()
				},
				immediate: true,
			}
		},
		beforeDestroy() {
			// Stop video (some browser keep playing even if the HTML node is removed)
			this.stop()
		},
		methods: {
			getType(scene) {
				if (!this.assets[scene.node_content] || !this.assets[scene.node_content].path)
					return null

				const path = this.assets[scene.node_content].path
				if (path.indexOf('mp4') > -1 || path.indexOf('m4v') > -1) {
					return 'video'
				}

				return 'static'
			},
			loadedData(index) {
				if (index != this.nextSceneIndex)
					return

				const oldIndex = this.currentSceneIndex

				// Update index
				this.currentSceneIndex = this.nextSceneIndex
				this.nextSceneIndex = -1

				this.$emit('loaded')

				setTimeout(() => {
					if (oldIndex != -1 && oldIndex != this.nextSceneIndex && oldIndex != this.currentSceneIndex) {
						// Clean old scene
						this.$set(this.scenes, oldIndex, {})
					}
				}, 1000)

				this.$nextTick(() => {
					this.checkPlayedState()
				})
			},
			checkPlayedState() {
				if (this.hasBeenPlayed) {
					this.stop()
				}
			},
			updatePlayState(index) {
				if (index == 0) {
					this.isPlaying = (this.$refs.video0 && !this.$refs.video0.paused)
				} else {
					this.isPlaying = (this.$refs.video1 && !this.$refs.video1.paused)
				}
			},
			playPause() {
				const video = (this.currentSceneIndex == 0 ? this.$refs.video0 : this.$refs.video1)

				if (video) {
					if (video.paused) {
						video.play()
					} else {
						video.pause()
					}
				}

				this.updatePlayState(this.currentSceneIndex)
			},
			restart() {
				const video = (this.currentSceneIndex == 0 ? this.$refs.video0 : this.$refs.video1)

				if (video) {
					if (video.paused) {
						video.currentTime = 0
						this.playPause()
					}
				}
			},
			stop() {
				const video = (this.currentSceneIndex == 0 ? this.$refs.video0 : this.$refs.video1)

				// Pause video and go to the end
				if (video) {
					video.pause()

					if (!isNaN(video.duration)) {
						video.currentTime = (video.duration - 0.2)
					}
					
					this.updatePlayState(this.currentSceneIndex)
				}
			},
			sceneEnded() {
				this.$emit('ended')
			}
		}
	}
</script>

<style lang="scss">
.scene {
	.button {
		bottom: 4rem;//todo
	}

	video {
		width: 100%;
		max-width: 100%;
		height: auto;
		max-height: 100%;
	}

	svg {
		max-width: 100%;
		height: auto;
		max-height: 100%;

		#faa-overlay {
			pointer-events: none;
			transform: translateZ(0);

			& > g {
				pointer-events: all;
				cursor: help;
				transform: translateZ(0);

				& > g:last-child {
					opacity: 0;
					transform: translateZ(0);
				}
			}

			&:hover {
				& > g {
					opacity: 0.4;
				}

				& > g:hover {
					opacity: 1;

					& > g:last-child {
						opacity: 1;
					}
				}
			}
		}
	}
}
</style>
